// Location: assets/bal-cart-delivery.js
// What: Manage cart delivery preferences and save them before checkout.
// Why: Keep regional choices and arrival dates valid as the cart changes.
// RELEVANT FILES: snippets/bal-cart-delivery.liquid, assets/bal-delivery-rules.js, tests/bal-cart-delivery.test.mjs
(() => {
  if (customElements.get('bal-cart-delivery')) return;
  const TIME_SLOTS = new Map([['午前中', 'before-noon'], ['14:00-16:00', '14-16'], ['16:00-18:00', '16-18'], ['18:00-20:00', '18-20'], ['19:00-21:00', '19-21']]);
  class BalCartDelivery extends HTMLElement {
    static values = null;
    static legacyPending = false;
    static initialValues(config) {
      const canonical = config.shipping || {};
      const date = canonical.date || config.values.date;
      const time = canonical.time ? [...TIME_SLOTS].find(([, code]) => code === canonical.time)?.[0] || '' : config.values.time;
      return { prefecture: config.values.prefecture, date: date === 'not-specified' ? '' : date, time };
    }
    static queue = Promise.resolve();
    static instances = new Set();
    static revision = 0;
    static checkout = false;
    static hasTerrine = false;
    static hasCool = false;
    static policy = {};
    static cartUpdating() {
      return Boolean(document.querySelector('.cart__items--disabled')) ||
        [...document.querySelectorAll('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')]
          .some(input => input.value !== input.getAttribute('value'));
    }
    static apply(values, hasTerrine, hasCool = this.hasCool) {
      this.hasCool = hasCool || hasTerrine;
      this.hasTerrine = hasTerrine;
      const cleaned = this.normalize(values, hasTerrine);
      if (JSON.stringify(cleaned) !== JSON.stringify(this.values)) this.revision++;
      this.values = cleaned;
      for (const instance of this.instances) {
        instance.hasTerrine = hasTerrine;
        instance.hasCool = this.hasCool;
        instance.render(values);
      }
      return cleaned;
    }
    static normalize(values, hasTerrine, now = new Date(), hasCool = this.hasCool) {
      const prefecture = BalDeliveryRules.prefectures.includes(values.prefecture) ? values.prefecture : '';
      const dates = BalDeliveryRules.getDeliveryDates({ ...this.policy, now, prefecture, hasTerrine, hasCool });
      const date = dates.includes(values.date) ? values.date : '';
      return { prefecture, date, time: date && TIME_SLOTS.has(values.time) ? values.time : '' };
    }
    static persist(values) {
      const attributes = { 'お届け先の都道府県': values.prefecture, 'shipandco-配達希望日': values.date || 'not-specified', 'shipandco-配達希望時間帯': TIME_SLOTS.get(values.time) || 'not-specified' };
      const run = async () => {
        const clearingLegacy = this.legacyPending;
        if (clearingLegacy) Object.assign(attributes, { '配送希望日': '', '配送希望時間帯': '' });
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes, sections: [...new Set([document.getElementById('main-cart-footer')?.dataset.id, ...[...this.instances].map(i => i.dataset.sectionId), 'cart-drawer'].filter(Boolean))], sections_url: `${window.Shopify.routes.root}cart` })
        });
        if (!response.ok) throw new Error('Unable to save delivery preferences');
        const cart = await response.json();
        if (clearingLegacy) this.legacyPending = false;
        return cart;
      };
      this.queue = this.queue.catch(() => {}).then(run);
      return this.queue;
    }
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
      BalCartDelivery.policy = { closedPeriods: this.config.closedPeriods };
      this.fields = Object.fromEntries(['prefecture', 'date', 'time'].map(key => [key, this.querySelector(`[data-field="${key}"]`)]));
      this.status = this.querySelector('[data-status]');
      this.hasTerrine = this.dataset.terrine === 'true';
      this.hasCool = this.dataset.cool === 'true';
      BalCartDelivery.instances.add(this);
      if (!BalCartDelivery.values) BalCartDelivery.legacyPending = this.config.hasLegacy;
      const initial = BalCartDelivery.values || BalCartDelivery.initialValues(this.config);
      this.fields.prefecture.replaceChildren(new Option(this.config.choose, ''), ...BalDeliveryRules.prefectures.map(p => new Option(p, p)));
      this.fields.prefecture.value = initial.prefecture;
      this.fields.date.dataset.selected = initial.date;
      this.fields.time.value = initial.time;
      BalCartDelivery.apply(initial, this.hasTerrine, this.hasCool);
      this.addEventListener('change', this.onChange = event => {
        if (!event.target.matches('[data-field]')) return;
        event.stopPropagation();
        this.notice = '';
        const values = this.read();
        BalCartDelivery.apply(values, this.hasTerrine);
        if (event.target === this.fields.prefecture) this.notice = '';
        this.save();
      });
      this.timer = setInterval(() => {
        const before = JSON.stringify(this.read());
        BalCartDelivery.apply(BalCartDelivery.values, BalCartDelivery.hasTerrine);
        if (before !== JSON.stringify(this.read())) this.save();
      }, 30000);
      this.unsubscribe = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.cartUpdate, () => { BalCartDelivery.revision++; this.save(); }) : null;
      this.save();
    }
    disconnectedCallback() {
      BalCartDelivery.instances.delete(this);
      clearInterval(this.timer);
      this.unsubscribe?.();
      this.removeEventListener('change', this.onChange);
    }
    read() {
      return Object.fromEntries(Object.entries(this.fields).map(([key, field]) => [key, field.value]));
    }
    render(values) {
      const cleaned = BalCartDelivery.normalize(values, this.hasTerrine, new Date(), this.hasCool);
      const dates = BalDeliveryRules.getDeliveryDates({ ...BalCartDelivery.policy, prefecture: cleaned.prefecture, hasTerrine: this.hasTerrine, hasCool: this.hasCool });
      this.fields.prefecture.value = cleaned.prefecture;
      this.fields.date.replaceChildren(new Option(this.config.noPreference, ''), ...dates.map(date => {
        const label = new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', month: 'long', day: 'numeric', weekday: 'short' }).format(new Date(`${date}T00:00:00+09:00`));
        return new Option(label, date);
      }));
      this.fields.date.value = cleaned.date;
      this.fields.time.value = cleaned.time;
      this.fields.date.disabled = !dates.length;
      this.fields.time.disabled = !cleaned.date;
      this.querySelector('[data-guidance]').textContent = !cleaned.prefecture ? this.config.chooseHelp : cleaned.prefecture === '沖縄県' ? this.config.unavailable : !dates.length ? this.config.noDates : '';
      this.querySelector('[data-delivery-notice]').textContent = this.hasTerrine ? this.config.terrineNotice : this.config.standardNotice;
      this.querySelector('[data-value="prefecture"]').value = cleaned.prefecture;
      this.querySelector('[data-shipco-date]').value = cleaned.date || 'not-specified';
      this.querySelector('[data-shipco-time]').value = TIME_SLOTS.get(cleaned.time) || 'not-specified';
      if (values.date && !cleaned.date) { this.notice = this.config.changed; this.status.textContent = this.notice; }
      return cleaned;
    }
    save() {
      if (!BalCartDelivery.saving) BalCartDelivery.saving = this.saveCurrent().finally(() => { BalCartDelivery.saving = null; });
      return BalCartDelivery.saving;
    }
    async saveCurrent() {
      for (const instance of BalCartDelivery.instances) instance.status.textContent = instance.config.saving;
      try {
        if (!BalDeliveryRules.isConfigurationValid(BalCartDelivery.policy)) throw new Error('Invalid delivery configuration');
        let revision;
        do {
          BalCartDelivery.apply(BalCartDelivery.values, BalCartDelivery.hasTerrine);
          revision = BalCartDelivery.revision;
          const cart = await BalCartDelivery.persist({ ...BalCartDelivery.values });
          let terrine, cool;
          for (const [section, html] of Object.entries(cart.sections || {})) {
            if (!html) continue;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const fresh = parsed.querySelector('bal-cart-delivery');
            if (fresh && ['true', 'false'].includes(fresh.dataset.terrine) && ['true', 'false'].includes(fresh.dataset.cool)) {
              terrine = fresh.dataset.terrine === 'true'; cool = fresh.dataset.cool === 'true';
              const config = fresh.querySelector('[data-delivery-config]');
              if (config) BalCartDelivery.policy = { closedPeriods: JSON.parse(config.textContent).closedPeriods };
            }
            const updated = parsed.querySelector('.free-shipping');
            const target = document.querySelector(section === 'cart-drawer' ? '#CartDrawer .free-shipping' : '#main-cart-footer .free-shipping');
            if (updated && target) target.replaceWith(updated);
          }
          if (terrine === undefined || !BalDeliveryRules.isConfigurationValid(BalCartDelivery.policy)) throw new Error('Unable to recheck cart delivery classification');
          BalCartDelivery.apply(BalCartDelivery.values, terrine, cool);
          const tail = BalCartDelivery.queue;
          await tail;
          if (tail !== BalCartDelivery.queue) revision = -1;
        } while (revision !== BalCartDelivery.revision);
        for (const instance of BalCartDelivery.instances) instance.status.textContent = instance.notice || '';
        return true;
      } catch {
        for (const instance of BalCartDelivery.instances) instance.status.textContent = BalDeliveryRules.isConfigurationValid(BalCartDelivery.policy) ? instance.config.error : instance.config.configurationError;
        return false;
      }
    }
  }
  customElements.define('bal-cart-delivery', BalCartDelivery);
  document.addEventListener('submit', async event => {
    if (!['cart', 'CartDrawer-Form'].includes(event.target.id) || event.submitter?.name !== 'checkout') return;
    const component = [...BalCartDelivery.instances].find(i => i.dataset.formId === event.target.id);
    if (!component || component.submitting) return;
    event.preventDefault();
    if (BalCartDelivery.checkout) return;
    if (BalCartDelivery.cartUpdating()) { component.status.textContent = component.config.saving; return; }
    BalCartDelivery.checkout = true;
    const button = event.submitter;
    button.disabled = true;
    const oldDate = BalCartDelivery.values.date;
    try {
      const saved = await component.save();
      if (!saved) { component.status.focus(); return; }
      if (BalCartDelivery.cartUpdating()) { component.status.textContent = component.config.saving; return; }
      if (oldDate && oldDate !== BalCartDelivery.values.date) {
        component.status.textContent = component.config.changed;
        component.fields.date.focus();
        return;
      }
      button.disabled = false;
      const active = [...BalCartDelivery.instances].find(i => i.dataset.formId === event.target.id) || component;
      const form = document.getElementById(event.target.id) || event.target;
      const submitter = button.form === form || form === event.target ? button : form.querySelector('[name="checkout"]') || document.querySelector(`[form="${form.id}"][name="checkout"]`);
      if (!submitter) return;
      active.submitting = true;
      try { form.requestSubmit(submitter); } finally { active.submitting = false; }
    } finally {
      button.disabled = false;
      BalCartDelivery.checkout = false;
    }
  }, true);
})();
